/**
 * PDF Export Service
 *
 * Generates professional PDF resumes using jsPDF with a clean, standardized layout
 */

import jsPDF from 'jspdf';
import { Resume } from './resume';

/**
 * Export resume to PDF with professional formatting
 */
export function exportResumeToPDF(resume: Resume): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = margin;

  // Colors
  const primaryColor: [number, number, number] = [31, 78, 121]; // Professional blue
  const secondaryColor: [number, number, number] = [100, 100, 100]; // Gray
  const textColor: [number, number, number] = [40, 40, 40]; // Dark gray

  // Helper function to add text with word wrap
  const addText = (text: string, x: number, y: number, options: {
    maxWidth?: number;
    fontSize?: number;
    bold?: boolean;
    color?: [number, number, number];
    align?: 'left' | 'center' | 'right';
  } = {}): number => {
    const fontSize = options.fontSize || 10;
    const color = options.color || textColor;
    const align = options.align || 'left';

    doc.setFontSize(fontSize);
    doc.setFont('helvetica', options.bold ? 'bold' : 'normal');
    doc.setTextColor(color[0], color[1], color[2]);

    if (options.maxWidth) {
      const lines = doc.splitTextToSize(text, options.maxWidth);
      lines.forEach((line: string, index: number) => {
        let xPos = x;
        if (align === 'center') {
          const textWidth = doc.getTextWidth(line);
          xPos = (pageWidth - textWidth) / 2;
        } else if (align === 'right') {
          const textWidth = doc.getTextWidth(line);
          xPos = pageWidth - margin - textWidth;
        }
        doc.text(line, xPos, y + (index * (fontSize * 0.4)));
      });
      return y + (lines.length * (fontSize * 0.4));
    } else {
      let xPos = x;
      if (align === 'center') {
        const textWidth = doc.getTextWidth(text);
        xPos = (pageWidth - textWidth) / 2;
      } else if (align === 'right') {
        const textWidth = doc.getTextWidth(text);
        xPos = pageWidth - margin - textWidth;
      }
      doc.text(text, xPos, y);
      return y + (fontSize * 0.4);
    }
  };

  // Helper function to add a section divider
  const addDivider = (y: number): number => {
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
    return y + 8;
  };

  // Helper function to add section header
  const addSectionHeader = (title: string, y: number): number => {
    yPos = addText(title.toUpperCase(), margin, y, {
      fontSize: 12,
      bold: true,
      color: primaryColor,
    });
    yPos = addDivider(yPos + 2);
    return yPos;
  };

  // NAME AND CONTACT INFO (Header)
  yPos = addText(resume.data.personalInfo.name, margin, yPos, {
    fontSize: 22,
    bold: true,
    color: primaryColor,
    align: 'center',
  });
  yPos += 3;

  // Contact information in one line
  const contactParts: string[] = [];
  if (resume.data.personalInfo.email) contactParts.push(resume.data.personalInfo.email);
  if (resume.data.personalInfo.phone) contactParts.push(resume.data.personalInfo.phone);
  if (resume.data.personalInfo.location) contactParts.push(resume.data.personalInfo.location);

  if (contactParts.length > 0) {
    yPos = addText(contactParts.join(' | '), margin, yPos, {
      fontSize: 9,
      color: secondaryColor,
      align: 'center',
    });
    yPos += 2;
  }

  // Links (LinkedIn, GitHub, Website)
  const linkParts: string[] = [];
  if (resume.data.personalInfo.linkedin) linkParts.push(resume.data.personalInfo.linkedin);
  if (resume.data.personalInfo.github) linkParts.push(resume.data.personalInfo.github);
  if (resume.data.personalInfo.website) linkParts.push(resume.data.personalInfo.website);

  if (linkParts.length > 0) {
    yPos = addText(linkParts.join(' | '), margin, yPos, {
      fontSize: 9,
      color: secondaryColor,
      align: 'center',
    });
  }

  yPos += 8;

  // PROFESSIONAL SUMMARY
  if (resume.data.summary) {
    yPos = addSectionHeader('Professional Summary', yPos);
    yPos = addText(resume.data.summary, margin, yPos, {
      maxWidth: contentWidth,
      fontSize: 10,
    });
    yPos += 8;
  }

  // EXPERIENCE
  if (resume.data.experience.length > 0) {
    yPos = addSectionHeader('Professional Experience', yPos);

    resume.data.experience.forEach((exp, index) => {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = margin;
      }

      // Position and Company
      yPos = addText(exp.position, margin, yPos, {
        fontSize: 11,
        bold: true,
      });

      // Company and location
      const companyLine = exp.location ? `${exp.company}, ${exp.location}` : exp.company;
      yPos = addText(companyLine, margin, yPos, {
        fontSize: 10,
        color: secondaryColor,
      });

      // Dates
      const dateRange = exp.endDate ? `${exp.startDate} - ${exp.endDate}` : `${exp.startDate} - Present`;
      yPos = addText(dateRange, margin, yPos, {
        fontSize: 9,
        color: secondaryColor,
      });
      yPos += 2;

      // Achievements (bullet points)
      if (exp.achievements && exp.achievements.length > 0) {
        exp.achievements.forEach((achievement) => {
          // Check if we need a new page for bullet
          if (yPos > 260) {
            doc.addPage();
            yPos = margin;
          }

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(textColor[0], textColor[1], textColor[2]);

          // Add bullet point
          doc.text('•', margin + 2, yPos);

          // Wrap achievement text
          const lines = doc.splitTextToSize(achievement, contentWidth - 8);
          lines.forEach((line: string, lineIndex: number) => {
            doc.text(line, margin + 7, yPos + (lineIndex * 4));
          });
          yPos += lines.length * 4 + 1;
        });
      }

      // Add spacing between experiences
      if (index < resume.data.experience.length - 1) {
        yPos += 4;
      }
    });
    yPos += 6;
  }

  // EDUCATION
  if (resume.data.education.length > 0) {
    if (yPos > 230) {
      doc.addPage();
      yPos = margin;
    }

    yPos = addSectionHeader('Education', yPos);

    resume.data.education.forEach((edu, index) => {
      if (yPos > 260) {
        doc.addPage();
        yPos = margin;
      }

      // Degree and Field
      yPos = addText(`${edu.degree} in ${edu.field}`, margin, yPos, {
        fontSize: 11,
        bold: true,
      });

      // Institution and location
      const institutionLine = edu.location ? `${edu.institution}, ${edu.location}` : edu.institution;
      yPos = addText(institutionLine, margin, yPos, {
        fontSize: 10,
        color: secondaryColor,
      });

      // Graduation date
      if (edu.graduationDate) {
        yPos = addText(edu.graduationDate, margin, yPos, {
          fontSize: 9,
          color: secondaryColor,
        });
      }

      // GPA if provided
      if (edu.gpa) {
        yPos = addText(`GPA: ${edu.gpa}`, margin, yPos, {
          fontSize: 9,
          color: secondaryColor,
        });
      }

      // Honors
      if (edu.honors && edu.honors.length > 0) {
        yPos += 1;
        edu.honors.forEach((honor) => {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(textColor[0], textColor[1], textColor[2]);
          doc.text('•', margin + 2, yPos);
          doc.text(honor, margin + 7, yPos);
          yPos += 3.5;
        });
      }

      if (index < resume.data.education.length - 1) {
        yPos += 4;
      }
    });
    yPos += 6;
  }

  // SKILLS
  // PROJECTS
  const projects = resume.data.projects ?? [];
  if (projects.length > 0) {
    if (yPos > 230) {
      doc.addPage();
      yPos = margin;
    }

    yPos = addSectionHeader('Projects', yPos);

    projects.forEach((project, index) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = margin;
      }

      // Project name
      yPos = addText(project.name, margin, yPos, {
        fontSize: 11,
        bold: true,
      });

      // Technologies
      if (project.technologies && project.technologies.length > 0) {
        yPos = addText(project.technologies.join(', '), margin, yPos, {
          fontSize: 9,
          color: secondaryColor,
          maxWidth: contentWidth,
        });
      }

      // URL
      if (project.url) {
        yPos = addText(project.url, margin, yPos, {
          fontSize: 9,
          color: secondaryColor,
          maxWidth: contentWidth,
        });
      }

      // Dates
      if (project.startDate || project.endDate) {
        const start = project.startDate || '';
        const end = project.endDate || 'Present';
        const dateRange = start ? `${start} - ${end}` : end;
        yPos = addText(dateRange, margin, yPos, {
          fontSize: 9,
          color: secondaryColor,
        });
      }

      // Description
      if (project.description) {
        yPos += 1;
        yPos = addText(project.description, margin, yPos, {
          maxWidth: contentWidth,
          fontSize: 10,
        });
      }

      // Achievements (bullet points)
      if (project.achievements && project.achievements.length > 0) {
        yPos += 2;
        project.achievements.forEach((achievement) => {
          if (yPos > 260) {
            doc.addPage();
            yPos = margin;
          }

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(textColor[0], textColor[1], textColor[2]);

          doc.text('•', margin + 2, yPos);
          const lines = doc.splitTextToSize(achievement, contentWidth - 8);
          lines.forEach((line: string, lineIndex: number) => {
            doc.text(line, margin + 7, yPos + (lineIndex * 4));
          });
          yPos += lines.length * 4 + 1;
        });
      }

      if (index < projects.length - 1) {
        yPos += 4;
      }
    });

    yPos += 6;
  }

  if (resume.data.skills.technical.length > 0 || resume.data.skills.soft.length > 0) {
    if (yPos > 240) {
      doc.addPage();
      yPos = margin;
    }

    yPos = addSectionHeader('Skills', yPos);

    if (resume.data.skills.technical.length > 0) {
      yPos = addText('Technical:', margin, yPos, {
        fontSize: 10,
        bold: true,
      });
      yPos = addText(resume.data.skills.technical.join(', '), margin + 25, yPos - 4, {
        maxWidth: contentWidth - 25,
        fontSize: 10,
      });
      yPos += 2;
    }

    if (resume.data.skills.soft.length > 0) {
      if (yPos > 270) {
        doc.addPage();
        yPos = margin;
      }
      yPos = addText('Soft Skills:', margin, yPos, {
        fontSize: 10,
        bold: true,
      });
      yPos = addText(resume.data.skills.soft.join(', '), margin + 25, yPos - 4, {
        maxWidth: contentWidth - 25,
        fontSize: 10,
      });
      yPos += 2;
    }

    if (resume.data.skills.languages && resume.data.skills.languages.length > 0) {
      if (yPos > 270) {
        doc.addPage();
        yPos = margin;
      }
      yPos = addText('Languages:', margin, yPos, {
        fontSize: 10,
        bold: true,
      });
      yPos = addText(resume.data.skills.languages.join(', '), margin + 25, yPos - 4, {
        maxWidth: contentWidth - 25,
        fontSize: 10,
      });
      yPos += 2;
    }

    if (resume.data.skills.certifications && resume.data.skills.certifications.length > 0) {
      if (yPos > 270) {
        doc.addPage();
        yPos = margin;
      }
      yPos = addText('Certifications:', margin, yPos, {
        fontSize: 10,
        bold: true,
      });
      yPos = addText(resume.data.skills.certifications.join(', '), margin + 25, yPos - 4, {
        maxWidth: contentWidth - 25,
        fontSize: 10,
      });
    }
  }

  // Generate filename
  const fileName = `${resume.data.personalInfo.name.replace(/\s+/g, '_')}_Resume.pdf`;

  // Save the PDF
  doc.save(fileName);
}
